import axios from 'axios';
import apiConfig from '../apiConfig';

export interface AdminProfile {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  avatar?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

class ProfileApiClass {
  private getAuthHeader() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private getMultipartAuthHeader() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'multipart/form-data',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // Get admin profile
  async getAdminProfile() {
    try {
      const response = await axios.get(
        `${apiConfig.apiBaseURL}/Admin-profile`,
        {
          headers: this.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching admin profile:', error);
      throw error;
    }
  }

  // Update admin profile (without avatar)
  async updateAdminProfile(data: UpdateProfileRequest) {
    try {
      const response = await axios.put(
        `${apiConfig.apiBaseURL}/update-admin-profile`,
        data,
        {
          headers: this.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating admin profile:', error);
      throw error;
    }
  }

  // Update admin profile with avatar
  async updateAdminProfileWithAvatar(data: UpdateProfileRequest, avatarFile: File) {
    try {
      const formData = new FormData();
      formData.append('firstName', data.firstName);
      formData.append('lastName', data.lastName);
      formData.append('phoneNumber', data.phoneNumber);
      formData.append('avatar', avatarFile);

      const response = await axios.put(
        `${apiConfig.apiBaseURL}/update-admin-profile`,
        formData,
        {
          headers: this.getMultipartAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating admin profile with avatar:', error);
      throw error;
    }
  }

  // Change admin password
  async changeAdminPassword(data: ChangePasswordRequest) {
    try {
      const response = await axios.put(
        `${apiConfig.apiBaseURL}/change-admin-password`,
        data,
        {
          headers: this.getAuthHeader(),
          // Treat 401 as a valid response to prevent interceptor from redirecting
          validateStatus: function (status) {
            return status >= 200 && status < 300 || status === 401;
          }
        }
      );

      // If we got a 401, return it as a failed response
      if (response.status === 401) {
        return {
          status: 'Failed',
          message: response.data?.message || 'Current password is incorrect'
        };
      }

      return response.data;
    } catch (error: any) {
      console.error('Error changing admin password:', error);
      throw error;
    }
  }
}

export const profileApi = new ProfileApiClass();
